// FundingProgramContent.jsx
import styles from './FundingMainContent.module.scss';
import { isNull } from 'lodash';
import { Space, Tag } from 'antd';
import { ReactComponent as HandIcon } from 'assets/icons/handIcon.svg';
import { ReactComponent as PercentageIcon } from 'assets/icons/percentageIcon.svg';
import { ReactComponent as FullCalendarIcon } from 'assets/icons/fullCalendarIcon.svg';
import { SIZE_MAP, COMPANY_TYPE_MAP, SUBSIBY_TYPE_MAP, LAW_CODE_MAP, EXPENSE_CLASS_CODE_MAP, LEGAL_FORM_CODES_MAP, buildFundingAmount, buildFundingPercentage, toCurrency } from '../../utils/mappers.js';

const FundingMainContent = ({ programDetails }) => {

  const parseExpenseTitle = (desc) => {
  const lines = desc
      .split('\n')
      .map(l => l.trim())
      .filter(l => l.length > 0);

    const title = lines[0];

    return title;
  };

  const parseExpenseDescription = (desc) => {
  const lines = desc
      .split('\n')
      .map(l => l.trim())
      .filter(l => l.length > 0);

    const items = lines.slice(1).filter(line =>
      /^\d+\./.test(line)
    );

    return items;
  };

  return (
    <main className={styles.content}>
      <section id="top" style={{marginBottom: 24}}>
        <h2 className={styles['program-title']}>
          {programDetails?.fundingSourceCode}
        </h2>

        <p className={styles['program-description']}>{programDetails.title}</p>

        <div className={styles['program-card-stats']} style={{ marginTop: 24 }}>
          {(programDetails.minFundingAmount && programDetails.maxFundingAmount) && (
            <div
              className={`${styles['program-card-stats-item']} ${styles.fundingAmount}`}
            >
              <HandIcon />
              <div className={styles['program-card-stats-details']}>
                <span>Επιδότηση</span>
                <p>{buildFundingAmount(programDetails.minFundingAmount, programDetails.maxFundingAmount)}</p>
              </div>
            </div>
          )}

          {programDetails.minPercentage && programDetails.maxPercentage && (
            <div
              className={`${styles['program-card-stats-item']} ${styles.fundingPercentage}`}
            >
              <PercentageIcon />
              <div className={styles['program-card-stats-details']}>
                <span>Ποσοστό Ενίσχυσης</span>
                <p>{buildFundingPercentage(programDetails.minPercentage,programDetails.maxPercentage)}</p>
              </div>
            </div>
          )}

          {programDetails.endOfSubmissionText && (
            <div
              className={`${styles['program-card-stats-item']} ${styles.deadline}`}
            >
              <FullCalendarIcon />
              <div className={styles['program-card-stats-details']}>
                <span>Προθεσμία</span>
                <p>{!isNull(programDetails.endOfSubmissionText) ? programDetails.endOfSubmissionText : '-'}</p>
              </div>
            </div>
          )}
        </div>

        {/* Company Sizes */}
        {programDetails?.companySizeCodes?.length > 0 && (
          <div style={{ marginTop: 12 }}>
            <div className={styles['program-card-company-size-title']}>
              Μέγεθος Επιχείρησης
            </div>
            <div style={{ marginTop: 12 }}>
              <Space wrap>
                {programDetails.companySizeCodes.map(code => (
                  <Tag
                    className={styles['program-card-company-size-tag']}
                    key={code}
                  >
                    {SIZE_MAP[code] ?? code}
                  </Tag>
                ))}
              </Space>
            </div>
          </div>
        )}
      </section>

      {/* 1. Σκοπός Προγράμματος */}
      <section
        id="purpose"
        // className={`${styles['content-card']} ${styles['content-section']}`}
      >
        <div className={styles['section-title-container']}>
          <span className={styles['section-title']}>Σκοπός Προγράμματος</span>
        </div>
        <div style={{ marginTop: 16 }}>
          <p className={styles['section-description']}>
            {programDetails.description}
          </p>
        </div>
      </section>

      {/* 2. Δικαιούχοι και Κριτήρια */}
      <section
        id="eligibility"
      >
        <div className={styles['section-title-container']}>
          <span className={styles['section-title']}>
            Δικαιούχοι και κριτήρια επιλεξιμότητας
          </span>
        </div>

        <div style={{marginTop: '16px'}}>
          <span className={styles['section-subtitle']}>Μεγέθη επιχείρησης: </span>
          <span className={styles['region-codes']}>
            {programDetails.companySizeCodes?.length ? 
              programDetails.companySizeCodes?.map(code => SIZE_MAP[code]).join(', ')
            : '-'}
          </span>
        </div>

        <div style={{marginTop: '16px'}}>
          <span className={styles['section-subtitle']}>Τόποι Επένδυσης: </span>
          <span className={styles['region-codes']}>
            {programDetails.regionCodes?.length ? 
              programDetails.regionCodes?.map(r => r.label).join(', ')
            : '-'}
          </span>
        </div>

        <p className={styles['section-subtitle']}>Τύπος επιχείρησης</p>
        <ul className={styles['kad-list']}>
          {programDetails.companyTypeCodes?.length ? 
            programDetails.companyTypeCodes.map(r => (
              <li key={r}>{COMPANY_TYPE_MAP[r]}</li>
            ))
          : '-'}
        </ul>

        <p className={styles['section-subtitle']}>Νομικές μορφές</p>
        <div className={styles['legal-form-codes']}>
          {programDetails.legalFormCodes?.length ? 
            programDetails.legalFormCodes?.map(r => LEGAL_FORM_CODES_MAP[r]).join(', ')
          : '-'}
        </div>

        <p className={styles['section-subtitle']}>Ενδεικτικοί ΚΑΔ</p>
        <ul className={styles['kad-list']}>
          {programDetails.kadCodes?.length ? 
            programDetails.kadCodes.map(r => (
              <li key={r.code}>{r.code + ' ' + r.description}</li>
            ))
          :'-'}
        </ul>
      </section>

      {/* 3. Επιλέξιμες δαπάνες */}
      <section
        id="expenses"
        className={`${styles['content-card']} ${styles['content-section']}`}
      >
        <div className={styles['section-title-container']}>
          <span className={styles['section-title']}>Επιλέξιμες δαπάνες</span>
        </div>
        {programDetails.expenses.map(exp => (
          <div key={exp.id} className={styles['expense-item']}>
            <p className={styles['expense-category']}>{exp.category}</p>
            <p className={styles['expense-subcategory']}>{exp.subcategory}</p>

            <p className={styles['expense-description']}><b>{parseExpenseTitle(exp.description)}</b></p>
            <ul className={styles['expense-description']}>
              {parseExpenseDescription(exp.description).map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
            <p className={styles['expense-description']}>
              <b>Κωδικός Δαπάνης:</b> {EXPENSE_CLASS_CODE_MAP[exp.expenseClassCode]}
            </p>
          </div>
        ))}
      </section>

      {/* 4. Χρηματοδοτικά στοιχεία */}
      <section
        id="financial"
        className={`${styles['content-card']} ${styles['content-section']}`}
      >
        <div className={styles['section-title-container']}>
          <span className={styles['section-title']}>Χρηματοδοτικά στοιχεία</span>
        </div>

        <div style={{marginTop: '16px'}}>
          <span className={styles['section-subtitle']}>Είδος ενίσχυσης: </span>
          <span className={styles['region-codes']}>
            {programDetails.subsidyTypeCodes?.length ?
              programDetails.subsidyTypeCodes?.map(r => SUBSIBY_TYPE_MAP[r]).join(', ')
            : '-'}
          </span>
        </div>

        <div style={{marginTop: '16px'}}>
          <span className={styles['section-subtitle']}>Καθεστώς ενίσχυσης: </span>
          <span className={styles['region-codes']}>
             {programDetails.laws?.length
              ? programDetails.laws.map(r => LAW_CODE_MAP[r.lawCode]).join(', ')
              : '-'}
          </span>
        </div>

        <div style={{marginTop: '16px'}}>
          <span className={styles['section-subtitle']}>Ποσοστό Ενίσχυσης: </span>
          <span className={styles['region-codes']}>
            {(programDetails.minPercentage && programDetails.maxPercentage) ? 
              buildFundingPercentage(programDetails.minPercentage,programDetails.maxPercentage)
            : '-'}
          </span>
        </div>

        <div style={{marginTop: '16px'}}>
          <span className={styles['section-subtitle']}>Μέγιστη Επιδότηση: </span>
          <span className={styles['region-codes']}>
            {programDetails.maxFundingAmount ?
              toCurrency(programDetails.maxFundingAmount)
            : '-'}
          </span>
        </div>

        <div style={{marginTop: '16px'}}>
          <span className={styles['section-subtitle']}>Ελάχιστη Επιδότηση: </span>
          <span className={styles['region-codes']}>
            {programDetails.minFundingAmount ?
              toCurrency(programDetails.minFundingAmount) 
            : '-'}
          </span>
        </div>

      </section>

      {/* 5. Προθεσμία */}
      <section
        id="timeline"
        className={`${styles['content-card']} ${styles['content-section']}`}
      >
        <div className={styles['section-title-container']}>
          <span className={styles['section-title']}>Προθεσμίες Υποβολής</span>
        </div>

        <div style={{marginTop: '16px'}}>
          <span className={styles['section-subtitle']}>Έναρξη Υποβολής Αιτήσεων: </span>
          <span className={styles['region-codes']}>
            {programDetails.startOfSubmission ?
              programDetails.startOfSubmission :
              '-'}
          </span>
        </div>

         <div style={{marginTop: '16px'}}>
          <span className={styles['section-subtitle']}>Λήξη Υποβολής Αιτήσεων: </span>
          <span className={styles['region-codes']}>
            {programDetails.endOfSubmission ?
              programDetails.endOfSubmission :
              '-'
            }
          </span>
        </div>

        <div style={{marginTop: '16px'}}>
          <span className={styles['section-subtitle']}>Περιγραφή Προθεσμίας: </span>
          <span className={styles['region-codes']}>
            {(programDetails.startOfSubmissionText & programDetails.endOfSubmissionText ) ?
              programDetails.startOfSubmissionText + ' - ' + programDetails.endOfSubmissionText
              : '-'}
          </span>
        </div>

      </section>

      {/* 6. Έγγραφα Προγράμματος (placeholder) */}
      {/* <section
        id="documents"
        className={`${styles['content-card']} ${styles['content-section']}`}
      >
        <h3>Έγγραφα Προγράμματος</h3>
        <p>Εδώ μπορούν να μπουν links / αρχεία του προγράμματος.</p>
      </section> */}
    </main>
  );
};

export default FundingMainContent;
